import { useEffect, useRef, useState } from 'react';
import type { Occurrence } from '../../../models/occurrence';
import type { TrackingView } from '../trackingUtils';
import type { OccurrenceState } from '../types';

const PAGE_SIZES: Record<TrackingView, number[]> = { table: [25, 50], board: [50], kanban: [10, 25] };
const DEFAULT_PAGE_SIZE: Record<TrackingView, number> = { table: 50, board: 50, kanban: 10 };
const SERVER_PAGE_SIZE = 50;

interface ITrackingPagingInput {
    occurrenceList: Occurrence[];
    totalOccurrences: number;
    getOccurrenceList: OccurrenceState['getOccurrenceList'];
    listLoading: boolean;
    loadingMoreData: boolean;
}

const useTrackingPaging = ({
    occurrenceList,
    totalOccurrences,
    getOccurrenceList,
    listLoading,
    loadingMoreData,
}: ITrackingPagingInput) => {
    const [view, setView] = useState<TrackingView>('table');
    const [uiPage, setUiPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE.table);
    const [pageWaiting, setPageWaiting] = useState(false);
    const previousList = useRef({ length: 0, firstId: '' });
    const prefetchedAt = useRef(-1);

    useEffect(() => {
        const firstId = occurrenceList[0]?.id ?? '';
        if (occurrenceList.length < previousList.current.length || firstId !== previousList.current.firstId) {
            setUiPage(0);
        }
        previousList.current = { length: occurrenceList.length, firstId };
    }, [occurrenceList]);

    useEffect(() => {
        if (listLoading) {
            prefetchedAt.current = -1;
        }
    }, [listLoading]);

    const prefetchTarget = Math.min(
        totalOccurrences,
        (Math.floor((uiPage * pageSize) / SERVER_PAGE_SIZE) + 2) * SERVER_PAGE_SIZE
    );

    useEffect(() => {
        if (view === 'board' || listLoading || loadingMoreData || pageWaiting) {
            return;
        }
        if (occurrenceList.length >= prefetchTarget || prefetchedAt.current === occurrenceList.length) {
            return;
        }
        prefetchedAt.current = occurrenceList.length;
        getOccurrenceList({}).catch(() => undefined);
    }, [view, listLoading, loadingMoreData, pageWaiting, occurrenceList.length, prefetchTarget]);

    const changePage = async (nextPage: number) => {
        const needed = (nextPage + 1) * pageSize;
        if (needed > occurrenceList.length && occurrenceList.length < totalOccurrences) {
            setPageWaiting(true);
            try {
                await getOccurrenceList({});
            } catch {
                return;
            } finally {
                setPageWaiting(false);
            }
        }
        setUiPage(nextPage);
    };

    const changePageSize = (size: number) => {
        setPageSize(size);
        setUiPage(0);
    };

    const changeView = (next: TrackingView) => {
        setView(next);
        setUiPage(0);
        setPageSize(DEFAULT_PAGE_SIZE[next]);
    };

    return {
        view,
        changeView,
        uiPage,
        pageSize,
        pageSizes: PAGE_SIZES[view],
        pageRows: occurrenceList.slice(uiPage * pageSize, (uiPage + 1) * pageSize),
        pageWaiting,
        changePage,
        changePageSize,
    };
};

export default useTrackingPaging;
